import { Button } from "@groot/ui/button";
import { Card, CardContent } from "@groot/ui/card";
import { Check, Trash2, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Set worker path for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface Selection {
  id: string;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  imageData?: string; // Base64 encoded image
}

interface PDFAnnotatorProps {
  pdfUrl: string;
  onSelectionsChange: (selections: Selection[]) => void;
  initialSelections?: Selection[];
}

export function PDFAnnotator({
  pdfUrl,
  onSelectionsChange,
  initialSelections = [],
}: PDFAnnotatorProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [selections, setSelections] = useState<Selection[]>(initialSelections);
  const [pageRenderTick, setPageRenderTick] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentRect, setCurrentRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [focusSelectionId, setFocusSelectionId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const [viewerWidth, setViewerWidth] = useState<number>(0);

  const setPageRef = (pageNumber: number) => (el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(pageNumber, el);
    else pageRefs.current.delete(pageNumber);
  };

  const setCanvasRef = (pageNumber: number) => (el: HTMLCanvasElement | null) => {
    if (el) canvasRefs.current.set(pageNumber, el);
    else canvasRefs.current.delete(pageNumber);
  };

  // Memoize options to prevent unnecessary reloads
  const options = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    }),
    [],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setViewerWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(el);
    setViewerWidth(Math.floor(el.clientWidth));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    onSelectionsChange(selections);
  }, [selections, onSelectionsChange]);

  useEffect(() => {
    if (focusSelectionId) {
      const t = setTimeout(() => setFocusSelectionId(null), 1500);
      return () => clearTimeout(t);
    }
  }, [focusSelectionId]);

  // Sync initial selections when prop changes (e.g., after statement load)
  useEffect(() => {
    if (initialSelections && initialSelections.length > 0) {
      setSelections(initialSelections);
      setCurrentPage(initialSelections[0].pageNumber);
      const el = pageRefs.current.get(initialSelections[0].pageNumber);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [initialSelections]);

  // Reset document loaded state when pdfUrl changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger on pdfUrl change
  useEffect(() => {
    setIsDocumentLoaded(false);
    setNumPages(0);
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsDocumentLoaded(true);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    setIsDocumentLoaded(false);
  };

  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>, pageNumber: number) => {
    const canvas = canvasRefs.current.get(pageNumber);
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    return { x, y };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>, pageNumber: number) => {
    const { x, y } = getCanvasCoordinates(e, pageNumber);

    setCurrentPage(pageNumber);
    setIsDrawing(true);
    setStartPoint({ x, y });
    setCurrentRect({ x, y, width: 0, height: 0 });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>, pageNumber: number) => {
    if (!isDrawing || !startPoint) return;
    if (currentPage !== pageNumber) return;

    const { x: currentX, y: currentY } = getCanvasCoordinates(e, pageNumber);

    const width = currentX - startPoint.x;
    const height = currentY - startPoint.y;

    setCurrentRect({
      x: startPoint.x,
      y: startPoint.y,
      width,
      height,
    });
  };

  const captureSelection = async (
    selection: { x: number; y: number; width: number; height: number },
    pageNumber: number,
  ) => {
    const pageContainer = pageRefs.current.get(pageNumber);
    const pageElement = pageContainer?.querySelector("canvas") as HTMLCanvasElement | undefined;
    if (!pageElement) return null;

    const tempCanvas = document.createElement("canvas");
    const ctx = tempCanvas.getContext("2d");
    if (!ctx) return null;

    const x = selection.width < 0 ? selection.x + selection.width : selection.x;
    const y = selection.height < 0 ? selection.y + selection.height : selection.y;
    const width = Math.abs(selection.width);
    const height = Math.abs(selection.height);

    tempCanvas.width = width;
    tempCanvas.height = height;

    ctx.drawImage(pageElement, x, y, width, height, 0, 0, width, height);

    return tempCanvas.toDataURL("image/png");
  };

  const handleMouseUp = async (pageNumber: number) => {
    if (!isDrawing || !currentRect || !startPoint) return;
    if (currentPage !== pageNumber) return;

    const normalizedRect = {
      x: currentRect.width < 0 ? startPoint.x + currentRect.width : startPoint.x,
      y: currentRect.height < 0 ? startPoint.y + currentRect.height : startPoint.y,
      width: Math.abs(currentRect.width),
      height: Math.abs(currentRect.height),
    };

    if (normalizedRect.width > 10 && normalizedRect.height > 10) {
      const imageData = await captureSelection(normalizedRect, pageNumber);

      const newSelection: Selection = {
        id: `${Date.now()}-${Math.random()}`,
        pageNumber,
        ...normalizedRect,
        imageData: imageData || undefined,
      };

      setSelections([...selections, newSelection]);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentRect(null);
  };

  const deleteSelection = (id: string) => {
    setSelections(selections.filter((s) => s.id !== id));
  };

  const clearAllSelections = () => {
    setSelections([]);
  };

  const jumpToSelection = (selection: Selection) => {
    setCurrentPage(selection.pageNumber);
    const el = pageRefs.current.get(selection.pageNumber);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    setFocusSelectionId(selection.id);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Redraw on scale/render tick changes
  useEffect(() => {
    const entries = Array.from(canvasRefs.current.entries());
    for (const [pageNumber, canvas] of entries) {
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const selection of selections.filter((s) => s.pageNumber === pageNumber)) {
        const isFocused = focusSelectionId === selection.id;
        ctx.strokeStyle = isFocused ? "#f59e0b" : "#3b82f6";
        ctx.lineWidth = isFocused ? 3 : 2;
        ctx.strokeRect(selection.x, selection.y, selection.width, selection.height);
        ctx.fillStyle = isFocused ? "rgba(245, 158, 11, 0.1)" : "rgba(59, 130, 246, 0.1)";
        ctx.fillRect(selection.x, selection.y, selection.width, selection.height);
      }
      if (currentRect && isDrawing && currentPage === pageNumber) {
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
        ctx.setLineDash([]);
        ctx.fillStyle = "rgba(59, 130, 246, 0.1)";
        ctx.fillRect(currentRect.x, currentRect.y, currentRect.width, currentRect.height);
      }
    }
  }, [selections, currentRect, isDrawing, currentPage, pageRenderTick, scale]);

  const handlePageRenderSuccess = (pageNumber: number) => {
    const pageContainer = pageRefs.current.get(pageNumber);
    const pageElement = pageContainer?.querySelector("canvas") as HTMLCanvasElement | undefined;
    const canvas = canvasRefs.current.get(pageNumber);
    if (pageElement && canvas) {
      canvas.width = pageElement.width;
      canvas.height = pageElement.height;
      canvas.style.width = `${pageElement.clientWidth}px`;
      canvas.style.height = `${pageElement.clientHeight}px`;
      setPageRenderTick((t) => t + 1);
    }
  };

  return (
    <div className="grid grid-cols-5 gap-4">
      <div className="col-span-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.max(0.5, scale - 0.1))}
            >
              <ZoomOut className="size-4" />
            </Button>
            <span className="text-sm font-medium">{Math.round(scale * 100)}%</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScale(Math.min(2.0, scale + 0.1))}
            >
              <ZoomIn className="size-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {selections.length} selection{selections.length !== 1 ? "s" : ""}
            </span>
            {selections.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearAllSelections}>
                <Trash2 className="size-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            <div
              ref={containerRef}
              className="relative overflow-auto"
              style={{ maxHeight: "70vh" }}
            >
              <div className="relative w-full">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  options={options}
                  loading={<div className="p-8 text-center">Loading PDF...</div>}
                  error={<div className="p-8 text-center text-destructive">Failed to load PDF</div>}
                >
                  {isDocumentLoaded &&
                    Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
                      <div key={pageNumber} ref={setPageRef(pageNumber)} className="relative mb-6">
                        <Page
                          pageNumber={pageNumber}
                          width={Math.max(100, Math.floor(viewerWidth))}
                          onRenderSuccess={() => handlePageRenderSuccess(pageNumber)}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                        <canvas
                          ref={setCanvasRef(pageNumber)}
                          className="absolute top-0 left-0 cursor-crosshair"
                          style={{ pointerEvents: "auto", zIndex: 10 }}
                          onMouseDown={(e) => handleMouseDown(e, pageNumber)}
                          onMouseMove={(e) => handleMouseMove(e, pageNumber)}
                          onMouseUp={() => handleMouseUp(pageNumber)}
                          onMouseLeave={() => {
                            if (isDrawing) {
                              setIsDrawing(false);
                              setStartPoint(null);
                              setCurrentRect(null);
                            }
                          }}
                        />
                      </div>
                    ))}
                </Document>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-sm text-muted-foreground">
          Click and drag to select transaction table areas. Selections can be made across pages.
        </div>
      </div>
      <div className="col-span-1">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium mb-2">Selected Areas</h3>
            <div className="space-y-2">
              {selections.length === 0 && (
                <div className="text-sm text-muted-foreground">No selections yet</div>
              )}
              {selections.map((selection, index) => (
                <button
                  key={selection.id}
                  type="button"
                  onClick={() => jumpToSelection(selection)}
                  className="w-full flex items-center justify-between p-2 border rounded-md hover:bg-muted"
                >
                  <div className="flex items-center gap-2 text-left">
                    <Check className="size-4 text-success" />
                    <span className="text-sm">
                      Selection {index + 1} - Page {selection.pageNumber}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({Math.round(selection.width)}x{Math.round(selection.height)})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSelection(selection.id);
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
